<template>
    <PageLoading :loading="loading">
        <!-- 错误页面 -->
        <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />

        <!-- 正常页面 -->
        <div v-else class="network-binding-page">
            <!-- 学校选择区域 -->
            <div class="school-selection-section">
                <div class="section-title">选择学校</div>
                <div class="school-input-row">
                    <van-field :model-value="selectedSchool?.name" placeholder="请输入或选择学校" readonly
                        @click="openSchoolPicker" right-icon="arrow" class="school-input" />
                    <van-button type="primary" class="forward-btn" plain size="small" icon="wechat" @click="handleForward">
                        转发
                    </van-button>
                </div>
            </div>

            <!-- 绑定状态统计 -->
            <div class="binding-stats">
                <div class="stat-card bound">
                    <div class="stat-label">已绑定</div>
                    <div class="stat-number">{{ boundCount }}个</div>
                </div>
                <div class="stat-card unbound">
                    <div class="stat-label">未绑定</div>
                    <div class="stat-number">{{ unboundCount }}个</div>
                </div>
            </div>

            <!-- 年级班级列表 -->
            <div class="grade-sections">
                <div v-for="grade in selectedSchool?.common_grade_arr" :key="grade.id" class="grade-section">
                    <div class="grade-header" @click="toggleGrade(grade.id)">
                        <span class="grade-title">{{ grade.name }}</span>
                        <van-icon :name="expandedGrades.includes(grade.id) ? 'arrow-up' : 'arrow-down'"
                            class="expand-icon" />
                    </div>

                    <div v-show="expandedGrades.includes(grade.id)" class="class-list">
                        <div v-for="classItem in grade.class_arr" :key="classItem.id" class="class-item">
                            <div class="class-info">
                                <div class="class-name">{{ classItem.name }}</div>
                                <div v-if="classItem.teacher_name" class="class-details">
                                    {{ classItem.teacher_name }} | {{ classItem.student_num }}人
                                </div>
                            </div>
                            <div class="class-actions">
                                <van-button v-if="currentUser?.is_agent && classItem.teacher_id" type="default" size="small"
                                    plain class="unbind-btn" @click="unbindClass(classItem)">
                                    解绑
                                </van-button>
                                <van-button v-if="!classItem.teacher_id && !currentUser?.is_collect && !currentUser?.is_principal && !currentUser?.is_agent && !currentUser?.is_student" type="primary" size="small" class="bind-btn"
                                    @click="bindClass(classItem)">
                                    绑定
                                </van-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 学校选择器 -->
            <SearchablePicker v-model:show="showSchoolPicker" title="选择学校" :options="schoolOptions"
                :value="selectedSchool" value-key="id" display-key="name" @confirm="onSchoolConfirm"
                @cancel="onSchoolCancel" />

            <!-- 绑定信息确认弹框 -->
            <van-popup v-model:show="showBindModal" position="center" round :style="{ width: '90%' }">
                <div class="bind-modal">
                    <div class="modal-header">
                        <span class="modal-title">绑定信息确认</span>
                    </div>

                    <div class="modal-content">
                        <div class="input-group">
                            <div class="input-label">老师姓名</div>
                            <van-field v-model="bindForm.teacherName" placeholder="请输入老师姓名" class="bind-input" />
                        </div>

                        <div class="input-group">
                            <div class="input-label">班级人数</div>
                            <van-field v-model="bindForm.studentCount" placeholder="请输入班级人数" type="number"
                                class="bind-input" />
                        </div>
                    </div>

                    <div class="modal-footer">
                        <van-button class="cancel-btn" round @click="showBindModal = false">取消</van-button>
                        <van-button class="confirm-btn" type="primary" round @click="confirmBind"
                            :disabled="!isBindFormValid">确认</van-button>
                    </div>
                </div>
            </van-popup>
        </div>
    </PageLoading>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { setWxTitle } from '@/utils/wx'
import SearchablePicker from '@/components/SearchablePicker.vue'
import PageLoading from '@/components/PageLoading.vue'
import ErrorPage from '@/components/ErrorPage.vue'
import { post } from '@/utils/request'
import { Dialog, Toast } from 'vant'
import { WECHAT_APPID } from '../../../config/env'
import { forwardToFriend, initWxShare } from '@/utils/wx'

onMounted(() => {
    setWxTitle('班级绑定')
    initPage()
    initWxSDK()
})

// 初始化微信JS-SDK
const initWxSDK = async () => {
    try {
        await initWxShare(this)
    } catch (error) {
        console.error('微信JS-SDK初始化失败:', error)
    }
}

// 加载状态
const loading = ref(true)
// 当前用户
const currentUser = ref(null)

// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

// 响应式数据
const showSchoolPicker = ref(false)
const showBindModal = ref(false)
const schoolOptions = ref([])
const selectedSchool = ref(null)
const expandedGrades = ref([])
const currentBindingClass = ref(null)
// 可以修改学校
const canEditSchool = ref(true)

// 绑定表单数据
const bindForm = ref({
    teacherName: '',
    studentCount: ''
})

const boundCount = ref(0)

const unboundCount = ref(0)

// 绑定表单验证
const isBindFormValid = computed(() => {
    return bindForm.value.teacherName.trim() && bindForm.value.studentCount.trim()
})

// 处理错误
const handleError = (title, message) => {
    hasError.value = true
    errorTitle.value = title
    errorMessage.value = message
    loading.value = false
}

// 初始化微信授权
const initWechatAuth = () => {
    // 获取当前url中的code参数
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')

    // 判断用户取消授权
    const error = urlParams.get('error')
    const errorDesc = urlParams.get('error_description')
    if (error) {
        console.log('用户取消授权:', errorDesc)
        handleError('获取用户信息失败', errorDesc)
        return
    }

    if (!code) {
        // 如果没有code，重定向到微信授权页面
        const redirectUri = encodeURIComponent(window.location.href)
        const appId = WECHAT_APPID
        window.location.href = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_userinfo&state=STATE#wechat_redirect`
    } else {
        // 有code则获取用户信息
        getUserInfo({
            code: code,
            app_id: WECHAT_APPID,
            page_type: '首页'
        })
    }
}

// 获取用户信息
const getUserInfo = async (data) => {
    try {
        const res = await post('/api/index/info', data)

        if (res.result && res.result.state === 'ok') {
            const userInfo = res.result.partner_info || {}
            currentUser.value = userInfo
            const accountInfo = res.result.account_info || {}
            // 存储用户ID到本地存储
            if (userInfo.id) {
                localStorage.setItem('userId', userInfo.id)
            }
            if (accountInfo.company_ids) {
                localStorage.setItem('company_ids', JSON.stringify(accountInfo.company_ids))
            }
            if (accountInfo.user_default_image) {
                localStorage.setItem('user_default_image', accountInfo.user_default_image)
            }
            getInitData()
        } else {
            handleError('获取用户信息失败', res.result?.message || '获取用户信息失败')
            loading.value = false
        }
    } catch (error) {
        console.error('获取用户信息失败:', error)
        handleError('获取用户信息失败', '获取用户信息失败，请重试')
        loading.value = false
    }
}

// 页面初始化
const initPage = async () => {
    try {
        // 检查是否已有用户ID
        if (localStorage.getItem('userId')) {
            getUserInfo({
                partner_id: parseInt(localStorage.getItem('userId')),
                app_id: WECHAT_APPID,
                page_type: '首页'
            })
        } else {
            initWechatAuth()
        }
    } catch (error) {
        console.error('页面初始化失败:', error)
        handleError('加载失败', '页面加载失败，请重试')
        loading.value = false
    }
}

const getInitData = async () => {
    try {
        // 这里可以调用API获取数据
        const urlParams = new URLSearchParams(window.location.search)
        const params = {
            company_ids: JSON.parse(localStorage.getItem('company_ids')),
            sale_id: urlParams.get('sale_id') ? parseInt(urlParams.get('sale_id')) : currentUser.value.id
        }
        if (urlParams.get('place_id')) {
            params.place_id = parseInt(urlParams.get('place_id'))
            canEditSchool.value = false
        }else if (selectedSchool.value) {
            params.place_id = selectedSchool.value.id
        }
        const res = await post('/api/select/child/partner/place', params)
        if (res.result && res.result.state === 'ok') {
            // 处理数据
            selectedSchool.value = res.result.place_info
            schoolOptions.value = res.result.place_arr
            boundCount.value = res.result.bind_num || 0
            unboundCount.value = res.result.un_bind_num || 0
        } else {
            handleError('获取数据失败', res.result?.message || '接口异常')
        }
    } catch (error) {
        console.error('获取数据失败:', error)
        handleError('获取数据失败', '获取数据失败，请重试')
    } finally {
        loading.value = false
    }
}

// 方法
const toggleGrade = (gradeId) => {
    const index = expandedGrades.value.indexOf(gradeId)
    if (index > -1) {
        expandedGrades.value.splice(index, 1)
    } else {
        expandedGrades.value.push(gradeId)
    }
}

const openSchoolPicker = () => {
    if (canEditSchool.value) {
        showSchoolPicker.value = true
    }
}

const onSchoolConfirm = (value) => {
    selectedSchool.value = value
    showSchoolPicker.value = false
    getInitData()
    // 这里可以根据选择的学校重新获取年级和班级数据
    console.log('选择的学校:', selectedSchool.value)
}

const onSchoolCancel = () => {
    showSchoolPicker.value = false
}

// 处理转发到群
const handleForward = () => {
    forwardToFriend({
        title: '班级绑定',
        desc: '查找您负责的班级，绑定即可查看班级信息',
        link: `${window.location.origin}/network-binding?place_id=${selectedSchool.value?.id}&sale_id=${currentUser.value.id}`,
        success: () => {
            Toast('请点击右上角分享给好友')
        },
        fail: () => {
            Toast('转发失败，请重试')
        }
    })
}

const bindClass = (classItem) => {
    currentBindingClass.value = classItem
    bindForm.value.teacherName = ''
    bindForm.value.studentCount = ''
    showBindModal.value = true
}

const confirmBind = async () => {
    if (!isBindFormValid.value) {
        return
    }
    loading.value = true
    try {
        const res = await post('/api/class/bind/tearcher', {
            partner_id: currentUser.value.id,
            class_id: currentBindingClass.value.id,
            type: '绑定',
            name: bindForm.value.teacherName,
            student_count: bindForm.value.studentCount
        })
        if (res.result && res.result.state === 'ok') {
            currentBindingClass.value.teacher_id = currentUser.value.id
            currentBindingClass.value.teacher_name = bindForm.value.teacherName
            currentBindingClass.value.student_num = bindForm.value.studentCount
            // 清空表单并关闭弹框
            bindForm.value.teacherName = ''
            bindForm.value.studentCount = ''
            currentBindingClass.value = null
            showBindModal.value = false
            // 跳到个人中心界面
            window.location.href = '/user'
            Toast.success('绑定成功')
        } else {
            Dialog.alert({
                title: '绑定失败',
                message: res.result?.message || '接口异常'
            })
        }
    } catch (error) {
        console.error('绑定失败:', error)
        Dialog.alert({
            title: '绑定失败',
            message: '接口请求异常，请重试'
        })
    } finally {
        loading.value = false
    }
}

const unbindClass = async (classItem) => {
    console.log('解绑班级:', classItem.name)
    loading.value = true
    // 这里可以调用API进行解绑操作
    try {
        const res = await post('/api/class/bind/tearcher', {
            partner_id: currentUser.value.id,
            class_id: classItem.id,
            type: '解绑'
        })
        if (res.result && res.result.state === 'ok') {
            classItem.teacher_id = 0
            classItem.teacher_name = ''
            classItem.student_num = 0
        } else {
            Dialog.alert({
                title: '解绑失败',
                message: res.result?.message || '接口异常'
            })
        }
    } catch (error) {
        console.error('解绑失败:', error)
        Dialog.alert({
            title: '解绑失败',
            message: '接口请求异常，请重试'
        })
    } finally {
        loading.value = false
    }
}

</script>

<style scoped>
.network-binding-page {
    background: #fff;
    min-height: calc(100vh - 32px);
    padding: 16px 11px;
}

.school-selection-section {
    margin-bottom: 16px;
}

.section-title {
    font-size: 14px;
    color: #4B5563;
    margin-bottom: 8px;
}

.school-input-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.school-input {
    border-radius: 8px;
    border: 1px solid rgba(229, 231, 235, 1);
    padding: 6px 12px;
    flex: 1;
}

.school-input :deep(.van-field__control) {
    background: transparent;
}

.forward-btn {
    padding: 17px 15px;
    font-size: 14px;
    border-radius: 9px;
}

.binding-stats {
    display: flex;
    gap: 12px;
    padding: 16px;
    margin-bottom: 16px;
    border-radius: 8px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.stat-card {
    flex: 1;
    padding: 16px;
    border-radius: 8px;
}

.stat-card.bound {
    background: #F0FDF4;
}

.stat-card.unbound {
    background: #FEFCE8;
}

.stat-label {
    font-size: 14px;
    color: #6B7280;
    margin-bottom: 6px;
}

.stat-number {
    font-size: 18px;
    font-weight: 500;
}

.stat-card.bound .stat-number {
    color: #16A34A;
}

.stat-card.unbound .stat-number {
    color: #CA8A04;
}

.grade-sections {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.grade-section {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.grade-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    background: #e0efed;
}

.grade-title {
    font-size: 16px;
    font-weight: 500;
}

.expand-icon {
    color: #9CA3AF;
    font-size: 16px;
}

.class-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    border-bottom: 1px solid #F3F4F6;
}

.class-item:last-child {
    border-bottom: none;
}

.class-info {
    flex: 1;
}

.class-name {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 4px;
}

.class-details {
    font-size: 14px;
    color: #4B5563;
}

.class-actions {
    margin-left: 16px;
}

.unbind-btn {
    border: 1px solid #D1D5DB;
    color: #4B5563;
    padding: 7px 15px;
    font-size: 14px;
    border-radius: 9999px;
}

.bind-btn {
    padding: 7px 15px;
    font-size: 14px;
    border-radius: 9999px;
}

/* 绑定弹框样式 */
.bind-modal {
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    padding: 21px;
}

.modal-header {
    margin-bottom: 21px;
}

.modal-title {
    font-size: 16px;
    font-weight: 500;
}

.input-group {
    margin-bottom: 14px;
}

.input-group:last-child {
    margin-bottom: 0;
}

.input-label {
    font-size: 12px;
    color: #4b5563;
    margin-bottom: 4px;
}

.bind-input {
    font-size: 12px;
    border-radius: 7px;
    border: 1px solid #e5e7eb;
    padding: 9px;
    line-height: unset;
}

.modal-footer {
    display: flex;
    gap: 12px;
    margin-top: 21px;
}

.cancel-btn,
.confirm-btn {
    flex: 1;
    height: 38px;
    font-size: 14px;
    border: none;
}

.cancel-btn {
    background: #f3f4f6;
    color: #4b5563;
}
</style>